import { Node, NodeProps } from "@xyflow/react";
import { useAtomValue, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { isRemirrorJSONEmpty } from "@zapier/rich-text-editor";
import { SyntheticEvent, useCallback, useEffect, useRef } from "react";
import classNames from "classnames";
import {
  ZapNodeData,
  ZapMetadata,
  isValidUUID,
  togglesAtomResettable,
  isTemplatePageAtom,
  isViewOnlyModeAtom,
  saveCanvasAsyncCommand,
  toggleZapStepNodesAndEdgesAtomFamily,
  nodesAtom,
} from "@zapier/canvas-state";

import { getItemsForStepSummary } from "../../../../utils/getItemsForStepSummary";
import { TimeSaved } from "../../../TimeMetrics";
import { StepSummary } from "../../../StepSummary";
import { AssetHeading } from "../../../AssetHeading";
import { FlexRow } from "../../../FlexRow";
import StepTitle from "../../../StepTitle/StepTitle";
import { StepStatusRow } from "../../../StepStatusRow";
import { DraftTag } from "../../DraftTag/DraftTag";
import { AssetNodeSkeleton } from "../AssetNodeSkeleton";

import { ZapMetrics } from "./ZapMetrics";
import styles from "./ZapNode.module.css";

interface ZapNodeContentProps {
  className: string;
  zap?: ZapMetadata;
  isZapNotFound?: boolean;
  onUpdateNode?: (
    data: Partial<ZapNodeData>,
    options?: { shouldSave: boolean },
  ) => void;
  children?: React.ReactNode;
  isViewOnlyMode?: boolean;
}

export function ZapNodeContentInner({
  data,
  id,
  selected = false,
  className,
  zap,
  onUpdateNode,
  children,
}: NodeProps<Node<ZapNodeData>> & ZapNodeContentProps) {
  const { zapId, label, actors, isExpanded } = data;
  const zapTitle = zap?.title ?? "";
  const hasNote = !isRemirrorJSONEmpty(data.notesJson);
  const hasSetZapTitleAsStepTitle = useRef(false);
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const isTemplatePage = useAtomValue(isTemplatePageAtom);
  const isDraftNode = !isValidUUID(id);
  const toggles = useAtomValue(togglesAtomResettable);
  const shouldShowDraftState =
    isDraftNode && toggles.aiCanvasComposer && !isTemplatePage;

  const steps = zap?.steps || [];
  const saveToBackend = useSetAtom(saveCanvasAsyncCommand);
  const toggleZapStepsAndEdges = useSetAtom(
    toggleZapStepNodesAndEdgesAtomFamily(id),
  );

  /** Persist whether Zap node is expanded, so we can show the same UI on page load.
   * React Flow state updates is handled by toggleZapStepsAndEdges */
  const saveZapNodeToBE = useAtomCallback(
    useCallback(
      async (get: (atom: typeof nodesAtom) => Node[]) => {
        const nodes = get(nodesAtom);
        const updatedNodes = nodes.map((node: Node) => {
          const isZapNode = node.id === id;

          if (!isZapNode) {
            return node;
          }

          return {
            ...node,
            data: {
              ...node.data,
              isExpanded: !isExpanded,
            },
          };
        });

        saveToBackend({ nodes: updatedNodes });
      },
      [id, isExpanded, saveToBackend],
    ),
  );

  const toggleButton = useCallback(
    (event: SyntheticEvent<HTMLButtonElement | HTMLUListElement>) => {
      /** To prevent the sidebar from opening */
      event.stopPropagation();
      toggleZapStepsAndEdges(Boolean(isExpanded));
      saveZapNodeToBE();
    },
    [isExpanded, saveZapNodeToBE, toggleZapStepsAndEdges],
  );

  useEffect(() => {
    // If there was no step title, set the Zap title as the step title
    if (
      !hasSetZapTitleAsStepTitle.current &&
      !label &&
      zapTitle &&
      onUpdateNode
    ) {
      onUpdateNode({ label: zapTitle }, { shouldSave: true });
      /* Mark hasSetZapTitleAsStepTitle as true to prevent this logic from running again.
       * We only want this to happen on page load, not when user empties the input manually */
      hasSetZapTitleAsStepTitle.current = true;
    }
  }, [label, zapTitle, onUpdateNode]);

  const stepSummaryItems = getItemsForStepSummary({
    actors,
    hasNote,
  });

  return (
    <>
      <div
        className={classNames(styles.wrapper, className, {
          [styles.draftNode]: shouldShowDraftState,
        })}
      >
        {shouldShowDraftState && <DraftTag assetType="Zap" />}
        {children}
        <StepStatusRow nodeId={id} assetType="zap" zapId={zapId} />
        <StepTitle
          nodeId={id}
          label={label}
          placeholder={!zapId ? "Choose or create a Zap" : undefined}
          isViewOnlyMode={isViewOnlyMode}
          isSelected={selected}
        />
        <FlexRow>
          <ZapMetrics zapId={zapId} isPublished={zap?.is_published} />
          <TimeSaved zapId={zapId} />
        </FlexRow>
        <AssetHeading
          nodeId={id}
          assetType="zap"
          isZapExpanded={isExpanded}
          onToggleZapSteps={toggleButton}
          zapSteps={steps}
        />
        {stepSummaryItems.length > 0 && (
          <StepSummary items={stepSummaryItems} isAssetNodeType />
        )}
      </div>
    </>
  );
}

/* This is a presentational component - please keep all RF smarts in the outer DefaultNode component
 * We use this to render a node outside of RF.
 * */
export function ZapNodeContent(
  props: NodeProps<Node<ZapNodeData>> & ZapNodeContentProps,
) {
  const toggles = useAtomValue(togglesAtomResettable);
  if (toggles.processAssetTypeNodes) {
    return (
      <AssetNodeSkeleton
        {...props}
        placeholder="Enter Zap title"
        iconName="miscBoltAltFill"
      />
    );
  }

  return <ZapNodeContentInner {...props} />;
}
